import { PUBLIC_STRIPE_KEY } from '$env/static/public'
import { captureException } from '@sentry/browser'
import { setContext, getContext } from 'svelte'
import { loadStripe } from '@stripe/stripe-js'
import { browser } from '$app/environment'
import { goto } from '$app/navigation'
import { page } from '$app/state'

import { track } from '$lib/plausible.svelte.js'

export class Payment {
  #successUrl
  // eslint-disable-next-line no-unused-private-class-members
  #clientSecret
  // eslint-disable-next-line no-unused-private-class-members
  #cancelUrl

  /** @type {import('@stripe/stripe-js').StripeElements?} */
  elements = $state(null)

  /** @type {import('@stripe/stripe-js').Stripe?} */
  stripe = $state(null)

  /** @type {Error?} */
  error = $state(null)

  constructor() {
    this.init()

    $effect(() => {
      if (this.error) {
        captureException(this.error)
        track('Exception', { props: { message: this.error.message } })
      }
    })
  }

  async init() {
    if (this.promise) return this.promise

    this.promise = browser
      ? loadStripe(PUBLIC_STRIPE_KEY)
          .then((stripe) => {
            if (stripe == null) throw new Error('Stripe failed to load')
            this.stripe = stripe
          })
          .catch((err) => {
            this.error = err
            throw err
          })
          .finally(() => (this.promise = null))
      : null

    return await this.promise
  }

  update({ clientSecret, successUrl, cancelUrl }) {
    if (!browser) return

    if (this.stripe == null) {
      return this.init().then(() =>
        this.update({ clientSecret, successUrl, cancelUrl })
      )
    }

    this.#clientSecret = clientSecret
    this.#successUrl = successUrl
    this.#cancelUrl = cancelUrl
    this.elements = null

    return this.stripe
      .retrievePaymentIntent(clientSecret)
      .then((result) => {
        if (result.error) throw result.error
        switch (result.paymentIntent.status) {
          case 'succeeded':
          case 'processing':
          case 'requires_capture': {
            try {
              if (page.url.searchParams.has('callback')) {
                const { currency, amount } = result.paymentIntent
                track('Purchase', {
                  revenue: {
                    currency: currency.toUpperCase(),
                    amount: amount / 100
                  }
                })
              }
            } finally {
              redirect(successUrl)
            }
            break
          }

          case 'canceled': {
            track('Cancel Payment')
            redirect(cancelUrl)
            break
          }

          case 'requires_confirmation':
          case 'requires_payment_method':
            break

          case 'requires_action':
            throw new Error('Waiting for payment authentication.')

          default:
            throw new Error(result.paymentIntent.status)
        }
      })
      .catch((err) => {
        this.error = err
        throw err
      })
  }

  async confirm() {
    if (this.stripe == null || this.elements == null) {
      if (this.promise == null) this.init()
      return this.promise.then(() => this.confirm())
    }

    const returnUrl = new URL(page.url)
    returnUrl.searchParams.set('callback', '')

    return this.stripe
      .confirmPayment({
        elements: this.elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: returnUrl.toString()
        }
      })
      .then((result) => {
        if (result.error) throw result.error
        try {
          const { currency, amount } = result.paymentIntent
          track('Purchase', {
            revenue: {
              currency: currency.toUpperCase(),
              amount: amount / 100
            }
          })
        } finally {
          redirect(this.#successUrl)
        }
      })
      .catch((err) => {
        this.error = err
        throw err
      })
  }

  dismiss() {
    this.error = null
  }
}

export const PAYMENT = Symbol('payment')

export function getPayment() {
  /** @type {Payment} */
  const store = getContext(PAYMENT)
  return store
}

export function setPayment() {
  const payment = new Payment()
  setContext(PAYMENT, payment)
  return payment
}

function redirect(url) {
  url = url.replace(page.url.origin, '')
  if (url.startsWith('/')) {
    goto(url)
  } else {
    window.location.assign(url)
  }
}
